// Shared helpers for AI Tutor page-context (question / note / video views).
// The tutor widget (components/AITutorChat) consumes:
//   window.__aiTutorContext + 'aitutor:context'  — which page the student is viewing
//   'aitutor:explain'                            — open the chat and submit a prompt
// Context shape (snake_case — the tutor backend accepts these keys):
//   { exam, exam_year, page_type, content_id, title, stem, page_url }

// Shared style for the per-page ✦ AI trigger buttons ("Explain my answer",
// "Ask AI about this note/lecture") — violet gradient treatment "B" (design 2026-07-08),
// matching the header + sidebar launchers. Applied via CustomButton's `style` prop with a
// neutral `btnColor` so the LMS blue `.btn-primary` never paints over it.
export const AI_TRIGGER_BTN_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  padding: '11.5px 17px',
  border: 'none',
  borderRadius: 10,
  color: '#fff',
  font: "700 13.5px 'Plus Jakarta Sans', system-ui, sans-serif",
  fontWeight: 900,
  background: 'linear-gradient(135deg,#6d4bff,#4a2fe0)',
  boxShadow: '0 4px 14px rgba(74,47,224,.4)',
  whiteSpace: 'nowrap',
};

// V1 scope gate: the tutor is CFA LEVEL 1 only (KB + entitlement are CFA-scoped).
// L1 family exam names: 'cfa', 'cfa_2022' … 'cfa_2027' (L2/L3 are 'cfa_2_*' / 'cfa_3*').
export function isAiTutorAvailable() {
  try {
    if (aiTutorCachedOff()) return false; // admin kill-switch — hide launchers on mount
    const sel = localStorage.getItem('selectedmenu') || '';
    return /^cfa(_\d{4})?$/.test(sel);
  } catch (e) {
    return false;
  }
}

// ── Admin kill-switch visibility (task A.2, design 2026-07-15) ────────────────────────
// The tutor can be disabled globally/per-course from the admin panel. The server enforces
// immediately (chat rejects ≤30s after a flip); this cache only removes dangling UI:
//   • isAiTutorAvailable() → false ⇒ route-mounted page buttons don't render;
//   • body class + the CSS below ⇒ once-mounted Header/Sidebar (and already-rendered page
//     buttons) hide live without a re-render.
// Key is per-exam (V2-ready). Synchronous localStorage read at module load = no flicker on
// repeat loads. Everything fails open: on any error the buttons stay visible.
var AI_TUTOR_OFF_KEY = 'apAiTutorOff:cfa1'; // V1 is CFA1-only, same as the launcher gate
var AI_TUTOR_OFF_CLASS = 'ap-ai-tutor-off';
var AI_TUTOR_PROBE_TS_KEY = 'apAiTutorProbeTs:cfa1'; // last /tutor/health probe (epoch ms)
var AI_TUTOR_PROBE_TTL = 60000; // re-probe at most once/min across page navigations

function aiTutorCachedOff() {
  try { return localStorage.getItem(AI_TUTOR_OFF_KEY) === '1'; } catch (e) { return false; }
}

// True if the last kill-switch probe is still fresh — lets the widget reuse the cached state
// instead of hitting /tutor/health on every page load (the complaint: fires per video page).
function aiTutorProbeFresh() {
  try {
    var ts = parseInt(localStorage.getItem(AI_TUTOR_PROBE_TS_KEY) || '0', 10);
    return ts > 0 && Date.now() - ts < AI_TUTOR_PROBE_TTL;
  } catch (e) { return false; }
}

/** Cached kill-switch state — lets the widget itself render nothing when the tutor is off. */
export function isAiTutorKillSwitchedOff() {
  return aiTutorCachedOff();
}

// Apply + remember the disabled state (also exported for the widget's own usage fetch).
export function setAiTutorOffState(off) {
  try {
    if (off) {
      localStorage.setItem(AI_TUTOR_OFF_KEY, '1');
      if (document.body) document.body.classList.add(AI_TUTOR_OFF_CLASS);
    } else {
      localStorage.removeItem(AI_TUTOR_OFF_KEY);
      if (document.body) document.body.classList.remove(AI_TUTOR_OFF_CLASS);
    }
  } catch (e) { /* noop */ }
}

// @deprecated 2026-07-24 — NO CALLERS. Used /tutor/usage just to read `enabled`, so it fired a
// usage request even when disabled. Superseded by refreshAiTutorAvailabilityPublic (/tutor/health).
export function refreshAiTutorAvailability(base, token, examKey) {
  try {
    if (!base || !token) return;
    var exam = String(examKey || 'cfa1').toLowerCase();
    var url = base + '/tutor/usage?exam=' + encodeURIComponent(exam) + '&_=' + Date.now();
    fetch(url, { headers: { Authorization: token }, cache: 'no-store' }).then(
      function (r) {
        // Only a SUCCESSFUL response carries the kill-switch. On 401/5xx the error body has no
        // `enabled`, so parsing it would evaluate `undefined === false` → false and CLEAR a real
        // admin disable on every page load. Bail out and keep whatever state is cached.
        if (!r.ok) return null;
        return r.json().then(
          function (d) { if (d) setAiTutorOffState(d.enabled === false); },
          function () { /* fail-open */ }
        );
      },
      function () { /* fail-open */ }
    );
  } catch (e) { /* noop */ }
}

// PRIMARY kill-switch probe — needs NO token. `/tutor/health` is public, so this keeps working
// even when the session/JWT is broken (a 401 on the guarded /tutor/usage must never be mistaken
// for "enabled"). `cb(off)` lets the widget drop its panel in the same tick.
// Older backends have no `enabled` field: we only act on a real boolean, so they stay visible.
export function refreshAiTutorAvailabilityPublic(base, examKey, cb) {
  try {
    if (!base) return;
    // Fresh cache (probed < TTL ago) → skip the network, just replay the cached state so the
    // widget still updates its `killed` flag. Cuts the per-navigation probe on video/note pages.
    if (aiTutorProbeFresh()) { if (cb) cb(aiTutorCachedOff()); return; }
    var exam = String(examKey || 'cfa1').toLowerCase();
    var url = base + '/tutor/health?exam=' + encodeURIComponent(exam) + '&_=' + Date.now();
    fetch(url, { cache: 'no-store' }).then(
      function (r) {
        if (!r.ok) return null; // unreachable/5xx → fail-open, keep the cached state
        return r.json().then(
          function (d) {
            if (!d || typeof d.enabled !== 'boolean') return;
            var off = d.enabled === false;
            setAiTutorOffState(off);
            try { localStorage.setItem(AI_TUTOR_PROBE_TS_KEY, String(Date.now())); } catch (e) { /* noop */ }
            if (cb) cb(off);
          },
          function () { /* fail-open */ }
        );
      },
      function () { /* fail-open */ }
    );
  } catch (e) { /* noop */ }
}

// Module load: apply the cached state before first paint + inject the launcher-hiding CSS
// from HERE (not the widget — the rule must exist even when the widget renders null).
(function initAiTutorOffState() {
  try {
    if (aiTutorCachedOff() && document.body) document.body.classList.add(AI_TUTOR_OFF_CLASS);
    var s = document.createElement('style');
    s.textContent = '.' + AI_TUTOR_OFF_CLASS + ' .ap-ai-launcher{display:none !important;}';
    document.head.appendChild(s);
  } catch (e) { /* noop */ }
})();

// Fallback context for chats opened outside a content page (dashboard, forum, …).
// `exam` is hardcoded on purpose: isAiTutorAvailable() gates the launcher to the CFA L1
// family, so by construction the selected exam here IS CFA1. When the tutor expands to
// other exams (V2+), extend BOTH together: the gate above + a selectedmenu→exam-slug map
// here (e.g. 'frm' → 'FRM1'), and the backend's exam-family entitlement scoping.
export function aiTutorGlobalContext() {
  return {
    exam: 'CFA1',
    exam_year: aiTutorExamYear(),
    page_url: aiTutorPageUrl(),
    page_type: 'global',
    title: 'CFA Level I',
  };
}

// Content pages call this on unmount so a later sidebar-opened chat doesn't
// inherit a stale page context (e.g. an old question on the dashboard).
export function clearAiTutorContext() {
  try {
    window.__aiTutorContext = null;
  } catch (e) {
    /* noop */
  }
}

// Year of the selected exam from the header switcher ('selectedmenu' in localStorage,
// e.g. 'cfa_2026' / 'cfa_2027'). Bare names without a year suffix ('cfa') → null.
export function aiTutorExamYear() {
  try {
    const sel = localStorage.getItem('selectedmenu') || '';
    // CFA1 editions: the 2026 exam is historically coded 'cfa_2025' (and bare 'cfa'); 2027 is
    // 'cfa_2027'. Normalise so exam_year matches the URL year (/cfa1/2026 vs /cfa1/2027) — the tutor
    // KB is keyed by this value, so it MUST agree with the URL the learner is on.
    if (sel === 'cfa_2027') return 2027;
    if (sel === 'cfa' || sel === 'cfa_2025' || sel === 'cfa_2026') return 2026;
    // Fallback for any other year-suffixed code (future L2/L3): its literal year.
    const m = sel.match(/_(\d{4})$/);
    return m ? parseInt(m[1], 10) : null;
  } catch (e) {
    return null;
  }
}

export function aiTutorPageUrl() {
  try {
    return window.location.href;
  } catch (e) {
    return null;
  }
}

// Publish the current page context: global (read when the tutor opens — timing-proof)
// + event (live-updates the context bar while the tutor is already open).
export function dispatchAiTutorContext(context) {
  try {
    window.__aiTutorContext = context;
    window.dispatchEvent(new CustomEvent('aitutor:context', { detail: { context } }));
  } catch (e) {
    /* noop */
  }
}

// Open the tutor and submit a prompt scoped to the given context (and optional item).
export function dispatchAiTutorExplain(detail) {
  try {
    window.dispatchEvent(new CustomEvent('aitutor:explain', { detail: detail }));
  } catch (e) {
    /* noop */
  }
}

// HTML → plain text, capped — the grounding snippet for KB retrieval.
export function aiTutorStem(html, max) {
  const cap = max || 400;
  try {
    return String(html || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, cap);
  } catch (e) {
    return '';
  }
}



// WEBPACK FOOTER //
// ./src/common/aiTutorContext.js